import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { simClient } from '../../simulation/client';

export function useSim() {
  return useSyncExternalStore(simClient.subscribe, simClient.getSnapshot);
}

/** Re-render at most every `ms` while a value changes rapidly (plots, tables). */
export function useThrottled<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  const last = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const now = performance.now();
    const wait = ms - (now - last.current);
    if (wait <= 0) {
      last.current = now;
      setV(value);
    } else {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        last.current = performance.now();
        setV(value);
      }, wait);
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [value, ms]);
  return v;
}

export function usePrefersReducedMotion(pref: 'system' | 'on' | 'off'): boolean {
  const [sys, setSys] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const on = () => setSys(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return pref === 'on' ? true : pref === 'off' ? false : !!sys;
}
